import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, updateDoc, onSnapshot, setDoc, collection, query, getDoc, arrayUnion } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { supabase } from '../lib/supabase';
import { endGame } from '../lib/gameService';
import { TopNav } from '../components/TopNav';
import { PlayerList } from '../components/PlayerList';
import { TotalsBar } from '../components/TotalsBar';
import { PokerTable } from '../components/PokerTable';
import { PlayerSelector } from '../components/PlayerSelector';
import { ConfirmModal } from '../components/ConfirmModal';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';
import { Plus, User, X, Square, StopCircle, Calendar, Clock, Layers, LogOut, Users, DollarSign, RotateCcw, RotateCw, Zap, ArrowRight, BookOpen } from 'lucide-react';
import clsx from 'clsx';
import { useCallback, useContext } from 'react';
import { SessionSubNav } from '../components/SessionSubNav';


// ─── Timer Hooks ──────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');

function useStopwatch(isoStart) {
    const calc = useCallback(() => {
        if (!isoStart) return { h: 0, m: 0, s: 0 };
        const elapsed = Math.max(0, Date.now() - new Date(isoStart));
        const totalSec = Math.floor(elapsed / 1000);
        return { h: Math.floor(totalSec / 3600), m: Math.floor((totalSec % 3600) / 60), s: totalSec % 60 };
    }, [isoStart]);

    const [parts, setParts] = useState(calc);
    useEffect(() => {
        if (!isoStart) return;
        setParts(calc());
        const id = setInterval(() => setParts(calc()), 1000);
        return () => clearInterval(id);
    }, [calc, isoStart]);
    return parts;
}

import { ChipCalculator } from '../components/ChipCalculator';

export const ActiveGame = ({ readOnly = false }) => {
    const { t, language, isRtl } = useLanguage();
    const { id, tIndex } = useParams();
    const navigate = useNavigate();
    
    // Auth & context
    const user = auth.currentUser;
    const hostId = user?.uid;
    const isGuest = user?.email === 'guest@ledger.com';

    // State declarations
    const [players, setPlayers] = useState([]);
    const [seatedPlayers, setSeatedPlayers] = useState(Array(11).fill(null));
    const [tableSeats, setTableSeats] = useState({});
    const [expenses, setExpenses] = useState([]);
    const [collected, setCollected] = useState([]);
    const [buyins, setBuyins] = useState([]); 
    const [cashouts, setCashouts] = useState([]);
    const [showCashoutForm, setShowCashoutForm] = useState(false);
    const [cashoutSlot, setCashoutSlot] = useState(null);
    const [cashoutAmount, setCashoutAmount] = useState('');
    const [showCashoutAllModal, setShowCashoutAllModal] = useState(false);
    const [cashoutAllAmounts, setCashoutAllAmounts] = useState({}); 
    const [showPlayerSelector, setShowPlayerSelector] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [attachedRestaurants, setAttachedRestaurants] = useState([]);
    const [showExpenseForm, setShowExpenseForm] = useState(false);
    const [showCollectedForm, setShowCollectedForm] = useState(false);
    const [showBuyinForm, setShowBuyinForm] = useState(false);
    const [showBuyinMethodModal, setShowBuyinMethodModal] = useState(false);
    const [buyinAmount, setBuyinAmount] = useState('');
    const [buyinPresets, setBuyinPresets] = useState([100, 200, 300, 400, 500, 1000]);
    const [isEditingPresets, setIsEditingPresets] = useState(false);
    const [buyinSlot, setBuyinSlot] = useState(null);
    const [movingFromIndex, setMovingFromIndex] = useState(null); 
    const [expenseAmount, setExpenseAmount] = useState('');
    const [expenseDesc, setExpenseDesc] = useState('');
    const [collectedAmount, setCollectedAmount] = useState('');
    const [collectedDesc, setCollectedDesc] = useState('');
    const [playerCount, setPlayerCount] = useState(10);
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [isTableOpen, setIsTableOpen] = useState(false);
    const [gameData, setGameData] = useState(null);
    const [showEndSessionModal, setShowEndSessionModal] = useState(false);
    const [endingSession, setEndingSession] = useState(false);
    const [selectedPlayerDetail, setSelectedPlayerDetail] = useState(null);
    const [otherTablePlayers, setOtherTablePlayers] = useState([]);
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
        type: 'warning'
    });

    // Effects
    useEffect(() => {
        if (!hostId || !activeSessionId) return;
        const fetchRestaurants = async () => {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const sessionSnap = await getDoc(sessionRef);
            if (sessionSnap.exists()) {
                const sData = sessionSnap.data();
                if (sData.attachedRestaurants?.length > 0) {
                    const resList = [];
                    for (const resId of sData.attachedRestaurants) {
                        const rSnap = await getDoc(doc(db, `hosts/${hostId}/restaurants/${resId}`));
                        if (rSnap.exists()) resList.push({ id: rSnap.id, name: rSnap.data().name });
                    }
                    setAttachedRestaurants(resList);
                }
            }
        };
        fetchRestaurants();
    }, [hostId, activeSessionId]);

    // Load game doc (new architecture) and resolve ledgerId → activeSessionId
    useEffect(() => {
        if (!hostId || !id) return;
        const loadGame = async () => {
            // Try to load from the new 'games' collection first
            const gameRef = doc(db, `hosts/${hostId}/games/${id}`);
            const gameSnap = await getDoc(gameRef);
            if (gameSnap.exists()) {
                const gd = { id: gameSnap.id, ...gameSnap.data() };
                setGameData(gd);
                // Use the linked ledgerId as the sessionId based on tIndex target
                if (tIndex === '2' && gd.ledgerId2) {
                    setActiveSessionId(gd.ledgerId2);
                    return;
                } else if (gd.ledgerId) {
                    setActiveSessionId(gd.ledgerId);
                    return;
                }
            }
            // Fallback: treat `id` as a direct session ID (old architecture / readOnly history)
            if (readOnly && id) {
                setActiveSessionId(id);
                return;
            }
            // Legacy: check if `id` exists in sessions directly
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${id}`);
            const sessionSnap = await getDoc(sessionRef);
            if (sessionSnap.exists()) {
                setActiveSessionId(id);
                return;
            }

            // Create-on-the-fly (only if not readOnly)
            if (!readOnly) {
                try {
                    const sessionId = `session_${Date.now()}`;
                    const newRef = doc(db, `hosts/${hostId}/sessions/${sessionId}`);
                    await setDoc(newRef, {
                        createdAt: new Date().toISOString(),
                        status: 'active',
                        playerCount: 10,
                        tableSeats: {},
                        buyins: [],
                        expenses: [],
                        collected: []
                    }, { merge: true });
                    setActiveSessionId(sessionId);
                } catch (error) {
                    console.error('Error creating session:', error);
                }
            }
        };
        loadGame();
    }, [hostId, id, readOnly]);

    // Subscribe to players list
    useEffect(() => {
        if (!hostId) return;
        const q = query(collection(db, `hosts/${hostId}/players`));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const playersList = [];
            snapshot.forEach((doc) => {
                playersList.push({ id: doc.id, ...doc.data() });
            });
            playersList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setPlayers(playersList);
        });
        return () => unsubscribe();
    }, [hostId]);

    // Subscribe to session updates
    useEffect(() => {
        if (!hostId || !activeSessionId) return;

        const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
        const unsubscribe = onSnapshot(sessionRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();

                // Sync table seats
                if (data.tableSeats) {
                    setTableSeats(data.tableSeats);
                    
                    const seatsArray = Array(11).fill(null);
                    Object.keys(data.tableSeats).forEach(seatKey => {
                        const index = parseInt(seatKey);
                        const seatValue = data.tableSeats[seatKey];
                        if (!isNaN(index) && index >= 1 && index <= 11 && seatValue) {
                            seatsArray[index - 1] = seatValue;
                        }
                    });
                    setSeatedPlayers(seatsArray);
                }

                // Sync buyins
                if (data.buyins) {
                    setBuyins(data.buyins);
                }

                // Sync expenses
                if (data.expenses) {
                    setExpenses(data.expenses);
                }

                // Sync collected
                if (data.collected) {
                    setCollected(data.collected);
                }

                // Sync cashouts
                if (data.cashouts) {
                    setCashouts(data.cashouts);
                }

                // Sync player count
                if (data.playerCount) {
                    setPlayerCount(data.playerCount);
                }

                // NEW: Sync seated count back to parent game doc for high-level stats
                const seatedCount = Object.values(data.tableSeats || {}).filter(Boolean).length;
                if (gameData && id) {
                    const gameRef = doc(db, `hosts/${hostId}/games/${id}`);
                    const field = tIndex === '2' ? 't2Players' : 't1Players';
                    if (gameData[field] !== seatedCount) {
                        updateDoc(gameRef, { [field]: seatedCount }).catch(console.error);
                    }
                }
            }
        });

        return () => unsubscribe();
    }, [hostId, activeSessionId, tIndex, id, gameData]);

    // ─── Subscribe to the OTHER table if it exists ───
    useEffect(() => {
        if (!hostId || !gameData) return;
        const otherId = activeSessionId === gameData.ledgerId ? gameData.ledgerId2 : gameData.ledgerId;
        if (!otherId) {
            setOtherTablePlayers([]);
            return;
        }

        const otherRef = doc(db, `hosts/${hostId}/sessions/${otherId}`);
        const unsubscribe = onSnapshot(otherRef, (snap) => {
            if (snap.exists()) {
                const seats = snap.data().tableSeats || {};
                setOtherTablePlayers(Object.values(seats).filter(Boolean));
            }
        });
        return () => unsubscribe();
    }, [hostId, gameData, activeSessionId]);

    const handleAddPlayer = (slotIndex) => {
        setSelectedSlot(slotIndex);
        setShowPlayerSelector(true);
    };

    const handleCreatePlayer = async (playerName) => {
        if (!hostId) return null;
        const trimmedName = playerName.trim();

        try {
            // Conflict Prevention: Duplicate name check
            const existing = (players || []).find(p => p.name?.toLowerCase() === trimmedName.toLowerCase());
            if (existing) {
                const alt = `${trimmedName} 2`;
                if (!window.confirm(t('alreadyExistsUse', { name: trimmedName, alt: alt }))) return null;
                return handleCreatePlayer(alt);
            }

            const playerId = `player_${Date.now()}`;
            const playerRef = doc(db, `hosts/${hostId}/players/${playerId}`);

            const newPlayer = {
                id: playerId,
                name: trimmedName,
                role: 'Player',
                totalProfitLoss: 0,
                totalBuyIns: 0,
                lifetimeValue: 0,
                debtLimit: 0,
                sessionsPlayed: 0,
                totalHoursPlayed: 0,
                hourlyWinRate: 0,
                lastPlayedDate: null,
                seatPreference: { hasPreference: false, seatNumber: null },
                mealPreference: { favoriteOrder: '', restaurantNotes: '' },
                reliabilityScore: 100,
                tardinessScore: 0,
                registrationStatus: 'not_registered',
                activeRegistrations: [],
                createdAt: new Date().toISOString()
            };

            await setDoc(playerRef, newPlayer);
            return newPlayer;
        } catch (error) {
            console.error('Error creating player:', error);
            return null;
        }
    };

    const handlePlayerSelected = async (player) => {
        if (!hostId || !activeSessionId) return;

        // Auto-assign to first empty seat if clicking the global +Add button
        let targetSlot = selectedSlot;
        if (targetSlot === null) {
            const emptyIdx = seatedPlayers.findIndex((p, idx) => p === null && idx < playerCount);
            if (emptyIdx === -1) {
                alert(t('tableFull') || 'The table is full');
                return;
            }
            targetSlot = emptyIdx + 1; // convert 0-based index -> 1-based seat ID
        }

        const playerData = {
            id: player.id,
            name: player.name,
            seatNumber: targetSlot,
            seatedAt: new Date().toISOString(),
        };

        // Optimistic local update — names appear immediately without waiting for Firestore
        if (typeof targetSlot === 'number') {
            const newSeated = [...seatedPlayers];
            newSeated[targetSlot - 1] = playerData;
            setSeatedPlayers(newSeated);
        }
        setTableSeats(prev => ({ ...prev, [targetSlot]: playerData }));
        setShowPlayerSelector(false);
        setSelectedSlot(null);

        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            pushUndo({ type: 'UPDATE_SEATS', tableSeats: { ...tableSeats } });
            await updateDoc(sessionRef, {
                [`tableSeats.${targetSlot}`]: playerData
            });
        } catch (error) {
            console.error('Error seating player:', error);
            // Revert optimistic update on failure
            if (typeof targetSlot === 'number') {
                const newSeated = [...seatedPlayers];
                newSeated[targetSlot - 1] = null;
                setSeatedPlayers(newSeated);
            }
            setTableSeats(prev => { const n = { ...prev }; delete n[targetSlot]; return n; });
        }
    };

    const pushUndo = (undoAction) => {
        setUndoStack(prev => [undoAction, ...prev].slice(0, 50));
        setRedoStack([]);
    };

    const handleUndo = async () => {
        if (undoStack.length === 0) return;
        const [action, ...remaining] = undoStack;
        setUndoStack(remaining);
        
        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const redoAction = await executeAction(action, sessionRef);
            if (redoAction) {
                setRedoStack(prev => [redoAction, ...prev]);
            }
        } catch (err) {
            console.error('Undo failed:', err);
        }
    };

    const handleRedo = async () => {
        if (redoStack.length === 0) return;
        const [action, ...remaining] = redoStack;
        setRedoStack(remaining);

        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const undoAction = await executeAction(action, sessionRef);
            if (undoAction) {
                setUndoStack(prev => [undoAction, ...prev]);
            }
        } catch (err) {
            console.error('Redo failed:', err);
        }
    };

    const executeAction = async (action, sessionRef) => {
        let inverse = null;
        switch (action.type) {
            case 'UPDATE_BUYINS':
                inverse = { type: 'UPDATE_BUYINS', buyins: [...buyins] };
                await updateDoc(sessionRef, { buyins: action.buyins });
                break;
            case 'UPDATE_EXPENSES':
                inverse = { type: 'UPDATE_EXPENSES', expenses: [...expenses] };
                await updateDoc(sessionRef, { expenses: action.expenses });
                break;
            case 'UPDATE_COLLECTED':
                inverse = { type: 'UPDATE_COLLECTED', collected: [...collected] };
                await updateDoc(sessionRef, { collected: action.collected });
                break;
            case 'UPDATE_CASHOUTS': {
                inverse = { type: 'UPDATE_CASHOUTS', cashouts: [...cashouts], tableSeats: { ...tableSeats } };
                const updates = { cashouts: action.cashouts };
                if (action.tableSeats) {
                    Object.keys(action.tableSeats).forEach(k => updates[`tableSeats.${k}`] = action.tableSeats[k]);
                }
                await updateDoc(sessionRef, updates);
                break;
            }
            case 'UPDATE_SEATS': {
                inverse = { type: 'UPDATE_SEATS', tableSeats: { ...tableSeats } };
                const seatUpdates = {};
                Object.keys(action.tableSeats).forEach(k => seatUpdates[`tableSeats.${k}`] = action.tableSeats[k]);
                await updateDoc(sessionRef, seatUpdates);
                break;
            }
        }
        return inverse;
    };

    const handleRemovePlayer = (slotKey) => {
        const playerName = tableSeats[slotKey]?.name || `מושב ${slotKey}`;
        setConfirmState({
            isOpen: true,
            title: t('removePlayer'),
            message: t('confirmRemovePlayer', { name: playerName }),
            type: 'danger',
            onConfirm: () => executeRemovePlayer(slotKey)
        });
    };

    const executeRemovePlayer = async (slotKey) => {
        setConfirmState(prev => ({ ...prev, isOpen: false }));
        if (!hostId || !activeSessionId) return;

        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            pushUndo({ type: 'UPDATE_SEATS', tableSeats: { ...tableSeats } });
            await updateDoc(sessionRef, {
                [`tableSeats.${slotKey}`]: null
            });
        } catch (error) {
            console.error('Error removing player:', error);
            if (typeof slotKey === 'number') {
                const newSeated = [...seatedPlayers];
                newSeated[slotKey - 1] = null;
                setSeatedPlayers(newSeated);
            }
        }
    };

    const handleAddBuyin = async (slotIndex, amount, method = 'cash') => {
        const player = seatedPlayers[slotIndex - 1];
        if (!player) return;

        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) return;

        // No confirmation - add immediately per user request
        executeAddBuyin(slotIndex, parsedAmount, method);
    };

    const executeAddBuyin = async (slotIndex, parsedAmount, method) => {
        setConfirmState(prev => ({ ...prev, isOpen: false }));
        setShowBuyinForm(false);
        setShowBuyinMethodModal(false);
        setBuyinAmount('');
        
        if (!hostId || !activeSessionId || !seatedPlayers[slotIndex - 1]) {
            setBuyinSlot(null);
            return;
        }
        
        const player = seatedPlayers[slotIndex - 1];
        const timestamp = new Date().toISOString();
        const buyinId = `buyin_${Date.now()}`;

        const newBuyin = {
            id: buyinId,
            playerId: player.id,
            playerName: player.name,
            slotIndex,
            amount: parsedAmount,
            method,
            timestamp
        };

        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const updatedBuyins = [...buyins, newBuyin];
            pushUndo({ type: 'UPDATE_BUYINS', buyins: [...buyins] });
            await updateDoc(sessionRef, {
                buyins: updatedBuyins
            });
            setBuyinSlot(null);

            // Sync to Master Ledger (Supabase)
            try {
                const { data: reg } = await supabase
                    .from('active_session_view')
                    .select('registration_id, buy_in_amount')
                    .eq('session_id', id)
                    .eq('player_name', player.name)
                    .maybeSingle();

                if (reg) {
                    const currentBuyin = Number(reg.buy_in_amount) || 0;
                    await supabase
                        .from('session_registrations')
                        .update({ buy_in_amount: currentBuyin + parsedAmount })
                        .eq('id', reg.registration_id);
                }
            } catch (syncErr) {
                console.error('Master Ledger Sync Error (Buy-in):', syncErr);
            }
        } catch (error) {
            console.error('Error adding buy-in:', error);
            setBuyins([...buyins, newBuyin]);
            setBuyinSlot(null);
        }
    };

    const handleAddRolePayout = async (role) => {
        const name = tableSeats[role]?.name;
        if (!name) {
            alert(t('addPlayerFirst'));
            return;
        }
        const amountStr = window.prompt(`Pay out amount for ${role === 'dealer' ? 'Dealer' : 'Floor'} (${name}):`);
        if (!amountStr) return;

        const amount = parseFloat(amountStr);
        if (isNaN(amount) || amount <= 0) return;

        const newExpense = {
            id: `exp_${Date.now()}`,
            description: `${role === 'dealer' ? 'Dealer' : 'Floor'} - ${name}`,
            amount: amount,
            category: role,
            timestamp: new Date().toISOString()
        };

        setExpenses(prev => [...prev, newExpense]);

        try {
            if (hostId && activeSessionId) {
                const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
                await updateDoc(sessionRef, {
                    expenses: arrayUnion(newExpense)
                });
            }
        } catch (error) {
            console.error('Error adding payout:', error);
        }
    };

    const handleAddExpense = () => {
        if (!expenseAmount) return;
        const amount = parseFloat(expenseAmount);
        const desc = expenseDesc.trim() || 'הוצאה';
        
        setConfirmState({
            isOpen: true,
            title: t('confirmExpense'),
            message: t('confirmExpenseMsg', { amount: amount.toLocaleString(), desc }),
            type: 'warning',
            onConfirm: () => executeAddExpense(amount, desc)
        });
    };

    const executeAddExpense = async (amount, desc) => {
        setConfirmState(prev => ({ ...prev, isOpen: false }));
        if (!hostId || !activeSessionId) return;

        const newExpense = {
            id: `exp_${Date.now()}`,
            description: desc,
            amount,
            timestamp: new Date().toISOString()
        };

        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const updatedExpenses = [...expenses, newExpense];
            pushUndo({ type: 'UPDATE_EXPENSES', expenses: [...expenses] });
            await updateDoc(sessionRef, {
                expenses: updatedExpenses
            });
            setExpenseAmount('');
            setExpenseDesc('');
            setShowExpenseForm(false);
        } catch (error) {
            console.error('Error adding expense:', error);
        }
    };

    const handleAddCollected = () => {
        if (!collectedAmount) return;
        const amount = parseFloat(collectedAmount);
        const desc = collectedDesc.trim() || 'גבייה';

        setConfirmState({
            isOpen: true,
            title: t('confirmCollection'),
            message: t('confirmCollectionMsg', { amount: amount.toLocaleString(), desc }),
            type: 'info',
            onConfirm: () => executeAddCollected(amount, desc)
        });
    };

    const executeAddCollected = async (amount, desc) => {
        setConfirmState(prev => ({ ...prev, isOpen: false }));
        if (!hostId || !activeSessionId) return;

        const newCollected = {
            id: `col_${Date.now()}`,
            description: desc,
            amount,
            timestamp: new Date().toISOString()
        };

        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const updatedCollected = [...collected, newCollected];
            pushUndo({ type: 'UPDATE_COLLECTED', collected: [...collected] });
            await updateDoc(sessionRef, {
                collected: updatedCollected
            });
            setCollectedAmount('');
            setCollectedDesc('');
            setShowCollectedForm(false);
        } catch (error) {
            console.error('Error adding collected:', error);
        }
    };

    const handleDeleteExpense = (id) => {
        const exp = expenses.find(e => e.id === id);
        if (!exp) return;
        
        setConfirmState({
            isOpen: true,
            title: t('deleteExpense'),
            message: t('confirmDeleteExpense', { amount: exp.amount.toLocaleString(), desc: exp.description }),
            type: 'danger',
            onConfirm: () => executeDeleteExpense(id)
        });
    };

    const executeDeleteExpense = async (id) => {
        setConfirmState(prev => ({ ...prev, isOpen: false }));
        if (!hostId || !activeSessionId) return;
        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const updatedExpenses = expenses.filter(e => e.id !== id);
            pushUndo({ type: 'UPDATE_EXPENSES', expenses: [...expenses] });
            await updateDoc(sessionRef, {
                expenses: updatedExpenses
            });
        } catch (error) {
            console.error('Error deleting expense:', error);
        }
    };

    const handleDeleteCollected = (id) => {
        const col = collected.find(e => e.id === id);
        if (!col) return;

        setConfirmState({
            isOpen: true,
            title: t('deleteCollection'),
            message: t('confirmDeleteCollection', { amount: col.amount.toLocaleString(), desc: col.description }),
            type: 'danger',
            onConfirm: () => executeDeleteCollected(id)
        });
    };

    const executeDeleteCollected = async (id) => {
        setConfirmState(prev => ({ ...prev, isOpen: false }));
        if (!hostId || !activeSessionId) return;
        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const updatedCollected = collected.filter(e => e.id !== id);
            pushUndo({ type: 'UPDATE_COLLECTED', collected: [...collected] });
            await updateDoc(sessionRef, {
                collected: updatedCollected
            });
        } catch (error) {
            console.error('Error deleting collected:', error);
        }
    };

    // ───────────────────────────────────────────────────────────────
    // CASHOUT LOGIC
    // ───────────────────────────────────────────────────────────────

    // Open cashout modal for a specific slot (1-based)
    const openCashoutForm = (slotKey) => {
        if (!seatedPlayers[slotKey - 1]) return;
        setCashoutSlot(slotKey);
        setCashoutAmount('');
        setShowCashoutForm(true);
    };

    // Confirm single cashout
    const handleCashout = (amountOverride = null) => {
        if (!cashoutSlot) return;
        const player = seatedPlayers[cashoutSlot - 1];
        if (!player) return;
        const amount = amountOverride !== null ? amountOverride : (parseFloat(cashoutAmount) || 0);

        setConfirmState({
            isOpen: true,
            title: t('confirmCashout'),
            message: t('confirmCashoutMsg', { name: player.name, amount: amount.toLocaleString() }),
            type: 'warning',
            onConfirm: () => executeCashout(amount)
        });
    };

    const executeCashout = async (amountOverride = null) => {
        setConfirmState(prev => ({ ...prev, isOpen: false }));
        if (!hostId || !activeSessionId || !cashoutSlot) return;
        const player = seatedPlayers[cashoutSlot - 1];
        if (!player) return;
        const amount = amountOverride !== null ? amountOverride : (parseFloat(cashoutAmount) || 0);
        const timestamp = new Date().toISOString();
        const cashoutId = `cashout_${Date.now()}`;

        const newCashout = {
            id: cashoutId,
            playerId: player.id,
            playerName: player.name,
            slotKey: cashoutSlot,
            amount,
            timestamp,
        };

        setShowCashoutForm(false);
        setCashoutAmount('');
        setCashoutSlot(null);

        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const updatedCashouts = [...cashouts, newCashout];
            pushUndo({ type: 'UPDATE_CASHOUTS', cashouts: [...cashouts], tableSeats: { ...tableSeats } });
            await updateDoc(sessionRef, {
                cashouts: updatedCashouts,
                [`tableSeats.${cashoutSlot}`]: null
            });

            // Sync to Master Ledger (Supabase)
            try {
                const { data: reg } = await supabase
                    .from('active_session_view')
                    .select('registration_id, cash_out_amount')
                    .eq('session_id', id)
                    .eq('player_name', player.name)
                    .maybeSingle();

                if (reg) {
                    const currentCashout = Number(reg.cash_out_amount) || 0;
                    await supabase
                        .from('session_registrations')
                        .update({ cash_out_amount: currentCashout + amount, status: 'cashed_out' })
                        .eq('id', reg.registration_id);
                }
            } catch (syncErr) {
                console.error('Master Ledger Sync Error (Cashout):', syncErr);
            }
        } catch (err) {
            console.error('Cashout error:', err);
        }
    };

    // Batch cashout all active players
    const openCashoutAll = () => {
        // Pre-fill with each player's total buy-in as default
        const amounts = {};
        seatedPlayers.forEach((p, idx) => {
            if (p) {
                const total = buyins
                    .filter(b => b.slotIndex === idx + 1)
                    .reduce((s, b) => s + b.amount, 0);
                amounts[idx + 1] = total > 0 ? String(total) : '';
            }
        });
        setCashoutAllAmounts(amounts);
        setShowCashoutAllModal(true);
    };

    const handleCashoutAll = () => {
        setConfirmState({
            isOpen: true,
            title: t('cashoutAllTitle'),
            message: t('cashoutAllMsg'),
            type: 'warning',
            onConfirm: () => executeCashoutAll()
        });
    };

    const executeCashoutAll = async () => {
        setConfirmState(prev => ({ ...prev, isOpen: false }));
        if (!hostId || !activeSessionId) return;
        
        const timestamp = new Date().toISOString();
        const newCashouts = [];
        const seatsToClear = {};

        seatedPlayers.forEach((player, idx) => {
            if (!player) return;
            const slotKey = idx + 1;
            const amount = parseFloat(cashoutAllAmounts[slotKey]) || 0;
            
            newCashouts.push({
                id: `cashout_${Date.now()}_${slotKey}`,
                playerId: player.id,
                playerName: player.name,
                slotKey,
                amount,
                timestamp
            });
            seatsToClear[`tableSeats.${slotKey}`] = null;
        });

        if (newCashouts.length === 0) {
            setShowCashoutAllModal(false);
            return;
        }

        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            setShowCashoutAllModal(false);
            setCashoutAllAmounts({});
            
            pushUndo({ type: 'UPDATE_CASHOUTS', cashouts: [...cashouts], tableSeats: { ...tableSeats } });
            await updateDoc(sessionRef, {
                cashouts: [...cashouts, ...newCashouts],
                ...seatsToClear
            });
        } catch (err) {
            console.error('Cashout all error:', err);
        }
    };

    // ───────────────────────────────────────────────────────────────
    // DERIVED: Cashed-Out Players gallery
    // Group cashouts by playerId, compute buy-in total vs cashout total
    // ───────────────────────────────────────────────────────────────
    const cashedOutPlayers = (() => {
        const map = {};
        cashouts.forEach(c => {
            if (!map[c.playerId]) {
                map[c.playerId] = {
                    playerId: c.playerId,
                    playerName: c.playerName,
                    totalBuyIn: 0,
                    totalCashout: 0,
                };
            }
            map[c.playerId].totalCashout += c.amount;
        });
        buyins.forEach(b => {
            if (map[b.playerId]) {
                map[b.playerId].totalBuyIn += b.amount;
            }
        });
        return Object.values(map);
    })();

    const handleDeleteBuyin = (id) => {
        const bin = buyins.find(b => b.id === id);
        if (!bin) return;

        setConfirmState({
            isOpen: true,
            title: t('deleteBuyin'),
            message: t('confirmDeleteBuyin', { name: bin.playerName, amount: bin.amount.toLocaleString() }),
            type: 'danger',
            onConfirm: () => executeDeleteBuyin(id)
        });
    };

    const executeDeleteBuyin = async (buyinId) => {
        setConfirmState(prev => ({ ...prev, isOpen: false }));
        if (!hostId || !activeSessionId) return;
        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            const updatedBuyins = buyins.filter(b => b.id !== buyinId);
            // Buyins share IDs with collected entries in the legacy system
            const updatedCollected = collected.filter(c => c.id !== buyinId); 
            pushUndo({ type: 'UPDATE_BUYINS', buyins: [...buyins] });
            await updateDoc(sessionRef, {
                buyins: updatedBuyins,
                collected: updatedCollected
            });
        } catch (error) {
            console.error('Error deleting buyin:', error);
        }
    };


    const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
    const totalCollected = collected.reduce((sum, c) => sum + c.amount, 0);

    const formatCurrency = (amount, sign = "") => {
        const absAmount = Math.abs(amount ?? 0).toFixed(0).toLocaleString();
        if (sign === "+") return `+ ₪ ${absAmount}`;
        if (sign === "-") return `- ₪ ${absAmount}`;
        return `₪ ${absAmount}`;
    };

    const handleEndSession = async () => {
        if (!hostId || !gameData?.id) return;
        setEndingSession(true);
        try {
            const netBalance = totalCollected - totalExpenses;
            const pc = seatedPlayers.filter(Boolean).length;
            await endGame(hostId, gameData.id, { netBalance, playerCount: pc });
            setShowEndSessionModal(false);
            navigate(`/session/${gameData.id}`);
        } catch (err) {
            console.error('Error ending session:', err);
        } finally {
            setEndingSession(false);
        }
    };

    const executeMove = async (fromSlot, toSlot) => {
        if (!hostId || !activeSessionId) return;
        if (fromSlot === toSlot) {
            setMovingFromIndex(null);
            return;
        }

        const builtSeats = { ...tableSeats };
        const playerAtFrom = builtSeats[String(fromSlot)];
        const playerAtTo = builtSeats[String(toSlot)];

        // Swap or Move
        builtSeats[String(toSlot)] = playerAtFrom;
        builtSeats[String(fromSlot)] = playerAtTo || null;

        setMovingFromIndex(null);

        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/${activeSessionId}`);
            
            // Also update all buyins for this player to the new slotIndex
            const updatedBuyins = buyins.map(b => {
                if (playerAtFrom && b.playerId === playerAtFrom.id) {
                    return { ...b, slotIndex: toSlot };
                }
                if (playerAtTo && b.playerId === playerAtTo.id) {
                    return { ...b, slotIndex: fromSlot };
                }
                return b;
            });

            await updateDoc(sessionRef, {
                tableSeats: builtSeats,
                buyins: updatedBuyins
            });
        } catch (err) {
            console.error("Move error:", err);
        }
    };

    const handleMoveStart = (slotKey) => {
        setMovingFromIndex(slotKey);
    };

    const handleMoveCancel = () => {
        setMovingFromIndex(null);
    };

    const { h, m, s } = useStopwatch(gameData?.startedAt || gameData?.startTime);
    const displayTableSeats = tableSeats;
    const displaySeatedPlayers = useMemo(() => {
        const arr = Array(11).fill(null);
        Object.keys(displayTableSeats).forEach(k => {
            const i = parseInt(k);
            if (!isNaN(i) && i >= 1 && i <= 11) arr[i - 1] = displayTableSeats[k];
        });
        return arr;
    }, [displayTableSeats]);

    // Game info helpers
    const gameDate = gameData?.startTime
        ? new Date(gameData.startTime).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })
        : null;

    const buyinSummary = {
        cash: buyins.filter(b => b.method === 'cash').reduce((s, b) => s + b.amount, 0),
        credit: buyins.filter(b => b.method === 'credit').reduce((s, b) => s + b.amount, 0),
        total: buyins.reduce((s, b) => s + b.amount, 0),
        collected: collected.reduce((s, c) => s + c.amount, 0),
        cashouts: cashouts.reduce((s, c) => s + c.amount, 0)
    };

    const chipsInPlay = buyinSummary.total - buyinSummary.collected - buyinSummary.cashouts;

    return (
        <>
            {/* Main Layout — Strict 'fixed' container to ensure zero page scroll. Everything must fit. */}
        <div className="fixed inset-0 flex flex-col overflow-hidden bg-slate-950 text-white font-outfit overscroll-none" dir={isRtl ? 'rtl' : 'ltr'}>
            {/* Header - Fixed Height */}
            <TopNav />
            <SessionSubNav sessionId={activeSessionId || id} />

            {/* Game Info Banner */}
            {gameData && (
                <div className="flex-none flex items-center justify-between gap-2 px-2 py-0.5 bg-slate-900/80 border-b border-white/5 backdrop-blur-sm z-40 relative">
                    <div className="flex items-center gap-2 min-w-0">
                        {gameDate && (
                            <div className="flex items-center gap-1 text-slate-400 text-[10px] hidden sm:flex">
                                <Calendar className="w-3 h-3 text-purple-500/80 flex-shrink-0" />
                                <span className="font-medium text-slate-200">{gameDate}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-1.5 px-2 bg-emerald-500/10 rounded-full border border-emerald-500/20 mr-1 shadow-[0_0_10px_rgba(52,211,153,0.1)]">
                            <Clock className="w-2.5 h-2.5 text-emerald-400 animate-pulse" />
                            <span className="font-mono text-[10px] font-black text-emerald-400 tabular-nums leading-none translate-y-[0.5px]">
                                {pad(h)}:{pad(m)}:{pad(s)}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2 bg-blue-500/10 rounded-full border border-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.1)]">
                            <Users className="w-2.5 h-2.5 text-blue-400" />
                            <span className="text-[10px] font-black text-blue-400 tabular-nums">
                                {seatedPlayers.filter(Boolean).length + otherTablePlayers.length}
                            </span>
                        </div>
                        {gameData.stakes && (
                            <span className="text-[9px] font-bold text-purple-400 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.5 rounded ml-1">
                                {gameData.stakes}
                            </span>
                        )}
                        {gameData.minBuyIn > 0 && (
                            <span className="text-[9px] font-bold text-slate-400 bg-slate-800 border border-slate-700 px-1.5 py-0.5 rounded tabular-nums">
                                {t('min')} ₪{gameData.minBuyIn.toLocaleString()}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {/* End Session button */}
                        {!readOnly && (
                            <button
                                onClick={() => setShowEndSessionModal(true)}
                                className="flex items-center gap-1 px-2 py-1 bg-rose-600/20 hover:bg-rose-600/40 border border-rose-500/30 text-rose-400 text-[10px] font-bold rounded flex-shrink-0"
                            >
                                <StopCircle className="w-3 h-3" />
                                {t('endSession') || 'סיים'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Ledger Section */}
            <main className={clsx(
                "flex-1 flex flex-col w-full max-w-7xl mx-auto px-1 pt-1 pb-6 gap-1 overflow-hidden min-h-0",
                isTableOpen ? "pb-1" : "pb-6"
            )}>
                {/* 1. Floor and Dealer - Side by side, thin at the top */}
                <div className="flex-none flex gap-1 w-full h-[30px] z-20 relative">
                    {/* Dealer Box */}
                    <div className="flex-1 bg-slate-900/60 backdrop-blur-md rounded-lg border border-blue-500/20 shadow-sm overflow-hidden flex justify-between items-center px-1.5">
                        <div className="flex items-center gap-1">
                            <Square className="w-2.5 h-2.5 text-blue-400" />
                            <h2 className="text-[9px] font-bold text-blue-400 uppercase hidden sm:block">{t('dealer')}</h2>
                            <div className="flex items-center" onClick={() => !readOnly && !tableSeats['dealer'] && handleAddPlayer('dealer')}>
                                {tableSeats['dealer'] ? (
                                    <span className="font-bold text-[10px] text-slate-200 truncate pr-0.5">{tableSeats['dealer'].name}</span>
                                ) : (
                                    <span className="text-[9px] text-slate-500 font-bold hover:bg-white/5 rounded px-1 cursor-pointer">+</span>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-1 items-center">
                            {tableSeats['dealer'] && !readOnly && (
                                <button onClick={() => handleAddRolePayout('dealer')} className="text-[8px] bg-blue-500/10 text-blue-400 font-bold px-1 py-0.5 rounded border border-blue-500/20 whitespace-nowrap">
                                    {t('paid')}
                                </button>
                            )}
                            {tableSeats['dealer'] && !readOnly && (
                                <button onClick={() => handleRemovePlayer('dealer')} className="text-slate-500 p-0.5 hidden xs:block">
                                    <X className="w-2.5 h-2.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Floor Box */}
                    <div className="flex-1 bg-slate-900/60 backdrop-blur-md rounded-lg border border-purple-500/20 shadow-sm overflow-hidden flex justify-between items-center px-1.5">
                         <div className="flex items-center gap-1">
                            <Square className="w-2.5 h-2.5 text-purple-400" />
                            <h2 className="text-[9px] font-bold text-purple-400 uppercase hidden sm:block">{t('floor')}</h2>
                            <div className="flex items-center" onClick={() => !readOnly && !tableSeats['floor'] && handleAddPlayer('floor')}>
                                {tableSeats['floor'] ? (
                                    <span className="font-bold text-[10px] text-slate-200 truncate pr-0.5">{tableSeats['floor'].name}</span>
                                ) : (
                                    <span className="text-[9px] text-slate-500 font-bold hover:bg-white/5 rounded px-1 cursor-pointer">+</span>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-1 items-center">
                            {tableSeats['floor'] && !readOnly && (
                                <button onClick={() => handleAddRolePayout('floor')} className="text-[8px] bg-purple-500/10 text-purple-400 font-bold px-1 py-0.5 rounded border border-purple-500/20 whitespace-nowrap">
                                    {t('paid')}
                                </button>
                            )}
                            {tableSeats['floor'] && !readOnly && (
                                <button onClick={() => handleRemovePlayer('floor')} className="text-slate-500 p-0.5 hidden xs:block">
                                    <X className="w-2.5 h-2.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* 2. Combined Player Management Grid */}
                <div className="flex-1 w-full bg-slate-900/40 backdrop-blur-md rounded-xl border border-purple-500/20 shadow-[0_0_18px_rgba(124,58,237,0.12)] overflow-hidden flex flex-col min-h-0 z-10">
                    <div className="flex-none h-8 flex items-center justify-between px-1.5 bg-slate-900/80 border-b border-purple-500/20 z-40 relative">
                        {/* Left Stats Section */}
                        <div className="flex items-center flex-1 min-w-0 overflow-x-auto no-scrollbar gap-1 text-purple-500 ml-2">
                            <div className="flex items-center flex-nowrap gap-1 bg-slate-900/50 p-0.5 rounded w-max min-w-min">
                                <div className="flex items-center gap-1 whitespace-nowrap bg-slate-800/40 px-1 py-0.5 rounded border border-white/5 shrink-0">
                                    <span className="text-[7px] font-black text-purple-500/60 uppercase">{t('cash')}</span>
                                    <span className="text-[9px] font-black text-emerald-400 tabular-nums" dir="ltr">{formatCurrency(buyinSummary.cash)}</span>
                                </div>
                                <div className="flex items-center gap-1 whitespace-nowrap bg-slate-800/40 px-1 py-0.5 rounded border border-white/5 shrink-0">
                                    <span className="text-[7px] font-black text-purple-500/60 uppercase">{t('credit')}</span>
                                    <span className="text-[9px] font-black text-yellow-400 tabular-nums" dir="ltr">{formatCurrency(buyinSummary.credit)}</span>
                                </div>
                                <div className="flex items-center gap-1 whitespace-nowrap bg-purple-500/10 px-1 py-0.5 rounded border border-purple-500/20 shrink-0">
                                    <span className="text-[7px] font-black text-purple-100 uppercase">{t('buyins')}</span>
                                    <span className="text-[9px] font-black text-purple-400 tabular-nums" dir="ltr">{formatCurrency(buyinSummary.total)}</span>
                                </div>
                                <div className="flex items-center gap-1 whitespace-nowrap bg-blue-500/10 px-1 py-0.5 rounded border border-blue-500/20 shrink-0">
                                    <span className="text-[7px] font-black text-blue-200 uppercase">{t('chips')}</span>
                                    <span className="text-[10px] font-black text-blue-400 underline decoration-blue-500/40 underline-offset-2 tabular-nums" dir="ltr">{formatCurrency(chipsInPlay)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right Buttons Section */}
                        <div className="flex shrink-0 gap-1 sm:gap-2">
                            <div className="flex gap-1">
                                <button
                                    onClick={handleUndo}
                                    disabled={undoStack.length === 0}
                                    className="p-1 sm:p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded border border-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    title={t('undo')}
                                >
                                    <RotateCcw className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                </button>
                                <button
                                    onClick={handleRedo}
                                    disabled={redoStack.length === 0}
                                    className="p-1 sm:p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded border border-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    title={t('redo')}
                                >
                                    <RotateCw className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                </button>
                            </div>

                            {/* Toggle 11th Player Button */}
                            {!readOnly && playerCount === 10 && (
                                <button
                                    onClick={async () => {
                                        if (isGuest) return;
                                        setPlayerCount(11);
                                        if (hostId && activeSessionId) {
                                            await updateDoc(doc(db, `hosts/${hostId}/sessions/${activeSessionId}`), { playerCount: 11 });
                                        }
                                    }}
                                    disabled={isGuest}
                                    className="px-1.5 sm:px-2 py-1 bg-slate-800 hover:bg-slate-700 text-[9px] sm:text-[10px] whitespace-nowrap font-bold text-purple-500 rounded border border-purple-500/20 transition-colors disabled:opacity-20 flex-shrink-0"
                                >
                                    + 11th
                                </button>
                            )}
                            {!readOnly && playerCount === 11 && (
                                <button
                                    onClick={async () => {
                                        if (isGuest) return;
                                        setPlayerCount(10);
                                        if (hostId && activeSessionId) {
                                            await updateDoc(doc(db, `hosts/${hostId}/sessions/${activeSessionId}`), { playerCount: 10 });
                                        }
                                    }}
                                    disabled={isGuest}
                                    className="px-1.5 sm:px-2 py-1 bg-slate-800 hover:bg-slate-700 text-[9px] sm:text-[10px] whitespace-nowrap font-bold text-slate-400 rounded border border-slate-700 transition-colors disabled:opacity-20 flex-shrink-0"
                                >
                                    - 11th
                                </button>
                            )}
                            {!readOnly && (
                                <button
                                    onClick={() => {
                                        setSelectedSlot(null);
                                        setShowPlayerSelector(true);
                                    }}
                                    className="px-1.5 sm:px-2 py-1 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-[9px] sm:text-[10px] whitespace-nowrap font-bold rounded flex items-center justify-center gap-1 shadow-lg shadow-purple-900/20 flex-shrink-0"
                                >
                                    <Plus className="w-3 h-3" />
                                    <span className="hidden xs:inline">{t('add')}</span>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0 relative flex flex-col no-scrollbar">
                        <PlayerList
                            players={displaySeatedPlayers}
                            buyins={buyins}
                            cashouts={cashouts}
                            cashedOutPlayers={cashedOutPlayers}
                            readOnly={readOnly}
                            movingFromIndex={movingFromIndex}
                            onMoveStart={handleMoveStart}
                            onMoveCancel={handleMoveCancel}
                            onAddPlayer={!readOnly ? (index) => {
                                if (movingFromIndex) {
                                    executeMove(movingFromIndex, index);
                                } else {
                                    setSelectedSlot(index);
                                    setShowPlayerSelector(true);
                                }
                            } : undefined}
                            onRemovePlayer={!readOnly ? (index) => handleRemovePlayer(index) : undefined}
                            onAddBuyin={!readOnly ? (slotIndex) => {
                                setBuyinSlot(slotIndex);
                                setBuyinAmount('');
                                setShowBuyinForm(true);
                            } : undefined}
                            onCashout={!readOnly ? (slotIdx) => {
                                setCashoutSlot(slotIdx);
                                setCashoutAmount('');
                                setShowCashoutForm(true);
                            } : undefined}
                            onCashoutAll={!readOnly ? openCashoutAll : undefined}
                            onPlayerClick={!readOnly ? (slotIdx, p, b) => {
                                if (movingFromIndex) {
                                    executeMove(movingFromIndex, slotIdx);
                                } else {
                                    setSelectedPlayerDetail({ slotIdx, player: p, buyins: b });
                                }
                            } : undefined}
                            onDeleteBuyin={!readOnly ? handleDeleteBuyin : undefined}
                            maxSlots={playerCount}
                        />
                    </div>
                </div>

                {/* 3. Expenses and Collected side by side ALWAYS */}
                <div className="flex-none flex flex-row gap-1 w-full max-h-[14vh] min-h-[90px] z-20 overflow-hidden">
                    
                    {/* Expenses */}
                    <div className="flex-1 flex flex-col min-w-0 bg-slate-900/60 backdrop-blur-md rounded-xl border border-rose-500/20 shadow-[0_0_12px_rgba(239,68,68,0.08)] overflow-hidden">
                        <div className="flex-none h-6 flex items-center justify-between px-2 bg-white/5 border-b border-white/5">
                            <h2 className="text-[9px] font-bold text-rose-400 uppercase truncate">
                                {t('expenses')}
                            </h2>
                            {!readOnly && (
                                <button
                                    onClick={() => setShowExpenseForm(true)}
                                    className="w-4 h-4 flex items-center justify-center rounded bg-rose-500/10 text-rose-500"
                                >
                                    <Plus className="w-2.5 h-2.5" />
                                </button>
                            )}
                        </div>

                        <div className="flex-none flex justify-between items-center px-2 py-0.5 border-b border-rose-500/20 bg-rose-500/5">
                            <div className="text-[7px] text-rose-400/80 uppercase font-black">{t('total')}</div>
                            <div className="text-[10px] font-black text-rose-500 tabular-nums whitespace-nowrap" dir="ltr">
                                {formatCurrency(totalExpenses, "-")}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-1 space-y-0.5 no-scrollbar">
                            {expenses.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-slate-500/50">
                                    <span className="text-[8px] uppercase">{t('noExpenses')}</span>
                                </div>
                            ) : (
                                expenses.slice().reverse().map(exp => (
                                    <div key={exp.id} className="relative flex items-center justify-between px-2 py-1 rounded bg-rose-500/10 border border-rose-500/5 group/entry overflow-hidden">
                                        <div className="flex-1 min-w-0 flex items-center gap-2 overflow-hidden" dir="ltr">
                                            <span className="text-[10px] font-black text-rose-400 tabular-nums shrink-0">
                                                {formatCurrency(Math.abs(parseFloat(exp.amount)))}
                                            </span>
                                            <span className="text-[9px] text-rose-100 font-bold truncate opacity-90">{exp.description}</span>
                                        </div>
                                        {!readOnly && (
                                            <button
                                                onClick={() => handleDeleteExpense(exp.id)}
                                                className="absolute -top-1 -right-1 w-3 h-3 bg-rose-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover/entry:opacity-100 z-10"
                                            >
                                                <X className="w-2 h-2" />
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Collected */}
                    <div className="flex-1 flex flex-col min-w-0 bg-slate-900/60 backdrop-blur-md rounded-xl border border-emerald-500/20 shadow-[0_0_12px_rgba(52,211,153,0.08)] overflow-hidden">
                        <div className="flex-none h-6 flex items-center justify-between px-2 bg-white/5 border-b border-white/5">
                            <h2 className="text-[9px] font-bold text-emerald-400 uppercase truncate">
                                {t('collected')}
                            </h2>
                            {!readOnly && (
                                <button
                                    onClick={() => setShowCollectedForm(true)}
                                    className="w-4 h-4 flex items-center justify-center rounded bg-emerald-500/10 text-emerald-500"
                                >
                                    <Plus className="w-2.5 h-2.5" />
                                </button>
                            )}
                        </div>

                        <div className="flex-none flex justify-between items-center px-2 py-0.5 border-b border-emerald-500/20 bg-emerald-500/5">
                            <div className="text-[7px] text-emerald-400/80 uppercase font-black">{t('total')}</div>
                            <div className="text-[10px] font-black text-emerald-500 tabular-nums whitespace-nowrap" dir="ltr">
                                {formatCurrency(totalCollected, "+")}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-1 space-y-0.5 no-scrollbar">
                            {collected.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-slate-500/50">
                                    <span className="text-[8px] uppercase">{t('noCollections')}</span>
                                </div>
                            ) : (
                                collected.slice().reverse().map(col => (
                                    <div key={col.id} className="relative flex items-center justify-between px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/5 group/entry overflow-hidden">
                                        <div className="flex-1 min-w-0 flex items-center gap-2 overflow-hidden" dir="ltr">
                                            <span className="text-[10px] font-black text-emerald-400 tabular-nums shrink-0">
                                                {formatCurrency(Math.abs(parseFloat(col.amount)))}
                                            </span>
                                            <span className="text-[9px] text-emerald-100 font-bold truncate opacity-90">{col.description}</span>
                                        </div>
                                        {!readOnly && (
                                            <button
                                                onClick={() => handleDeleteCollected(col.id)}
                                                className="absolute -top-1 -right-1 w-3 h-3 bg-rose-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover/entry:opacity-100 z-10"
                                            >
                                                <X className="w-2 h-2" />
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                </div>
            </main>

            {/* Totals Bar & Drawer Handle */}
            <div className="flex-none flex-shrink-0 bg-slate-900/80 backdrop-blur-sm relative z-[200] transition-all duration-300">
                <div className="py-0.5 border-t border-b border-purple-500/20 flex flex-col items-center justify-center relative shadow-lg">
                    <TotalsBar collected={totalCollected} expenses={totalExpenses} />

                    {/* Dynamic Drawer Handle */}
                    <button
                        onClick={() => setIsTableOpen(!isTableOpen)}
                        className="absolute -top-6 inset-x-0 mx-auto w-32 py-1 bg-slate-800 border border-slate-700/50 rounded-t-xl text-purple-500 text-[9px] font-bold tracking-widest uppercase flex items-center justify-center gap-1 shadow-[0_-5px_15px_rgba(0,0,0,0.5)] z-[250]"
                    >
                        {isTableOpen ? (
                            <>{t('closeTable')} ▼</>
                        ) : (
                            <>{t('openTable')} ▲</>
                        )}
                    </button>
                </div>
            </div >

            {/* Poker Table Section - Animated Drawer */}
            <div
                className={
                    clsx(
                        "flex-none w-full bg-slate-950 relative overflow-hidden flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
                        isTableOpen ? "h-[38dvh] sm:h-[40dvh] md:h-[42dvh] lg:h-[44dvh] opacity-100 pb-4" : "h-0 opacity-0 pb-0"
                    )
                }
            >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black opacity-80"></div>
                {/* Scale up the table slightly without breaking the height constraint */}
                <div className="w-full max-w-xs sm:max-w-xl md:max-w-2xl lg:max-w-3xl xl:max-w-4xl max-h-full h-full relative z-0">
                    <PokerTable
                        tableData={{ id: activeSessionId, seats: displayTableSeats }}
                        onSeatClick={playerCount === 10 || playerCount === 11 ? handleAddPlayer : () => { }}
                        playerCount={playerCount}
                        allPlayers={players}
                        readOnly={readOnly}
                    />
                </div>

                {/* Floor – pinned to bottom-right of the drawer, outside the table oval */}
                <button
                    onClick={() => !readOnly && handleAddPlayer('floor')}
                    className={clsx(
                        'absolute bottom-2 right-2 z-30',
                        'w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex flex-col items-center justify-center gap-0',
                        'border-2 shadow-xl hover:scale-110 transition-all duration-300',
                        tableSeats['floor']
                            ? 'bg-purple-600 border-purple-400 text-white shadow-purple-900/50'
                            : 'bg-slate-900/80 backdrop-blur-sm border-slate-700 text-slate-400 hover:border-purple-500/50 hover:text-purple-500 hover:bg-slate-800'
                    )}
                >
                    {tableSeats['floor'] ? (
                        <span className="text-[9px] font-bold truncate px-1 text-center font-heebo leading-tight w-10 flex items-center justify-center">
                            {tableSeats['floor'].name}
                        </span>
                    ) : (
                        <>
                            <span className="text-sm font-light leading-none font-inter">+</span>
                            <span className="text-[8px] font-bold uppercase tracking-tighter opacity-60 font-inter">{t('floor')}</span>
                        </>
                    )}
                </button>
            </div >


            {/* Modals placed here to ensure they are on top */}
            {
                showPlayerSelector && (
                    <PlayerSelector
                        players={players.filter(p => {
                            const isSeatedInTable = Object.values(tableSeats).some(sp => sp && sp.id === p.id);
                            const isSeatedElsewhere = otherTablePlayers.some(otp => otp && otp.id === p.id);
                            return !isSeatedInTable && !isSeatedElsewhere;
                        })}
                        onSelect={handlePlayerSelected}
                        onCreatePlayer={handleCreatePlayer}
                        onClose={() => {
                            setShowPlayerSelector(false);
                            setSelectedSlot(null);
                        }}
                    />
                )
            }

            {/* ── Single Cashout Modal ── */}
            {showCashoutForm && cashoutSlot && seatedPlayers[cashoutSlot - 1] && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-hidden">
                    <div className="w-full max-w-sm h-fit">
                        <ChipCalculator 
                            title={t('cashout') || 'פרישה'}
                            onCancel={() => { setShowCashoutForm(false); setCashoutSlot(null); }}
                            onConfirm={(amount) => {
                                setCashoutAmount(String(amount));
                                handleCashout(amount);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* ── Cashout All Modal ── */}
            {showCashoutAllModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-slate-900 border border-purple-500/30 rounded-xl p-5 w-full max-w-sm shadow-2xl space-y-4 animate-in fade-in zoom-in duration-200 max-h-[85dvh] flex flex-col">
                        <div className="flex items-center gap-2 flex-none">
                            <Users className="w-4 h-4 text-purple-400" />
                            <h3 className="text-base font-bold text-purple-400 font-heebo">{t('cashoutAll')}</h3>
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                            {seatedPlayers.map((player, idx) => {
                                if (!player) return null;
                                const slotKey = idx + 1;
                                const totalIn = buyins.filter(b => b.slotIndex === slotKey).reduce((s, b) => s + b.amount, 0);
                                return (
                                    <div key={slotKey} className="bg-slate-800/60 rounded-lg p-3 space-y-1.5">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-bold text-white font-heebo">{player.name}</span>
                                            {totalIn > 0 && (
                                                <span className="text-[10px] text-slate-500 font-inter tabular-nums">
                                                    {t('buyIn')}: ₪{totalIn.toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                        <input
                                            type="number"
                                            value={cashoutAllAmounts[slotKey] ?? ''}
                                            onChange={e => setCashoutAllAmounts(prev => ({ ...prev, [slotKey]: e.target.value }))}
                                            className="w-full bg-slate-900 border border-purple-500/20 focus:border-purple-500/50 rounded-lg px-3 py-2 text-white text-base font-bold focus:outline-none transition-all tabular-nums"
                                            placeholder="₪0"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex gap-3 flex-none">
                            <button
                                onClick={() => setShowCashoutAllModal(false)}
                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors border border-slate-700 font-heebo"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handleCashoutAll}
                                className="flex-1 bg-purple-600 hover:bg-purple-500 text-white py-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors shadow-lg shadow-purple-900/20 font-heebo"
                            >
                                {t('confirmAll')}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {
                (showExpenseForm || showCollectedForm) && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                        <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 w-full max-w-xs shadow-2xl space-y-4 animate-in fade-in zoom-in duration-200">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                {showCollectedForm ? <span className="text-emerald-400">{t('addCollected')}</span> :
                                        <span className="text-rose-400">{t('addExpense')}</span>}
                            </h3>

                             <div>
                                <label className="block text-xs text-slate-400 mb-2">{t('description')}</label>
                                
                                {/* Description presets */}
                                <div className="flex gap-2 mb-3 pr-2 overflow-x-auto no-scrollbar">
                                    {(showCollectedForm ? ['House Fee', 'Tips'] : [...attachedRestaurants.map(r => r.name), 'Dealer', 'Floor', 'Food', 'Other']).map(desc => (
                                        <button
                                            key={desc}
                                            onClick={() => showCollectedForm ? setCollectedDesc(desc) : setExpenseDesc(desc)}
                                            className={clsx(
                                                "px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all whitespace-nowrap",
                                                showCollectedForm 
                                                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                                                    : "bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20"
                                            )}
                                        >
                                            {desc}
                                        </button>
                                    ))}
                                </div>

                                <input
                                    type="text"
                                    value={showCollectedForm ? collectedDesc : expenseDesc}
                                    onChange={(e) => showCollectedForm ? setCollectedDesc(e.target.value) : setExpenseDesc(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm focus:border-poker-gold outline-none focus:ring-1 focus:ring-poker-gold placeholder-slate-600 transition-all"
                                    placeholder="Description..."
                                    autoFocus
                                />
                            </div>
                            
                            <div>
                                <label className="block text-xs text-slate-400 mb-2">{t('amount')}</label>
                                
                                {/* Amount presets */}
                                <div className="grid grid-cols-3 gap-2 mb-3">
                                    {[50, 100, 200, 500, 1000, 2000].map((preset) => (
                                        <button
                                            key={preset}
                                            onClick={() => {
                                                if (showCollectedForm) {
                                                    const current = Number(collectedAmount) || 0;
                                                    setCollectedAmount(String(current + preset));
                                                } else {
                                                    const current = Number(expenseAmount) || 0;
                                                    setExpenseAmount(String(current + preset));
                                                }
                                            }}
                                            className={clsx(
                                                "bg-slate-800 border rounded-lg p-2 text-xs font-bold transition-all tabular-nums shadow-sm",
                                                showCollectedForm 
                                                    ? "hover:bg-emerald-900/20 border-emerald-500/20 text-emerald-100" 
                                                    : "hover:bg-rose-900/20 border-rose-500/20 text-rose-100"
                                            )}
                                            dir="ltr"
                                        >
                                            +{preset}
                                        </button>
                                    ))}
                                </div>

                                <input
                                    type="number"
                                    value={showCollectedForm ? collectedAmount : expenseAmount}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (showCollectedForm) setCollectedAmount(val);
                                        else setExpenseAmount(val);
                                    }}
                                    className={clsx(
                                        "w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-lg font-bold focus:outline-none focus:ring-1 transition-all text-center",
                                        showCollectedForm ? "focus:border-emerald-500 focus:ring-emerald-500" :
                                            "focus:border-rose-500 focus:ring-rose-500"
                                    )}
                                    placeholder="0"
                                />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => {
                                        setShowExpenseForm(false);
                                        setShowCollectedForm(false);
                                        setExpenseAmount('');
                                        setCollectedAmount('');
                                    }}
                                    className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors border border-slate-700"
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    onClick={() => {
                                        if (showCollectedForm) {
                                            handleAddCollected();
                                            setShowCollectedForm(false);
                                        } else {
                                            handleAddExpense();
                                        }
                                    }}
                                    className={clsx(
                                        "flex-1 py-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors shadow-lg",
                                        showCollectedForm ? "bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-900/20" :
                                            "bg-rose-600 hover:bg-rose-500 text-white shadow-rose-900/20"
                                    )}
                                >
                                    {t('confirm')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ── Add Buy-in Modal ── */}
            {showBuyinForm && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-hidden">
                    <div className="w-full max-w-sm h-fit">
                        <ChipCalculator 
                            title={t('addBuyin') || 'הוסף קנייה'}
                            onCancel={() => { setShowBuyinForm(false); setBuyinSlot(null); }}
                            onConfirm={(amount) => {
                                setBuyinAmount(String(amount));
                                setShowBuyinForm(false);
                                setShowBuyinMethodModal(true);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* ── Buy-in Method Modal (Step 2) ── */}
            {showBuyinMethodModal && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-slate-900 border border-purple-500/40 rounded-xl p-6 w-full max-w-xs shadow-2xl space-y-6 animate-in fade-in zoom-in duration-200">
                        <div className="text-center">
                            <h3 className="text-xl font-bold text-white font-heebo mb-1">{t('paymentMethod')}</h3>
                            <p className="text-purple-500 font-bold text-lg">₪{Number(buyinAmount).toLocaleString()}</p>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            <button
                                onClick={() => handleAddBuyin(buyinSlot, buyinAmount, 'cash')}
                                className="group relative overflow-hidden bg-emerald-600 hover:bg-emerald-500 text-white p-4 rounded-xl transition-all shadow-lg active:scale-95 flex flex-col items-center gap-1"
                            >
                                <span className="text-lg font-bold font-heebo relative z-10">{t('cash')}</span>
                                <span className="text-[10px] opacity-80 uppercase tracking-widest relative z-10">Cash</span>
                                <div className="absolute inset-0 bg-gradient-to-tr from-black/20 to-transparent"></div>
                            </button>

                            <button
                                onClick={() => handleAddBuyin(buyinSlot, buyinAmount, 'credit')}
                                className="group relative overflow-hidden bg-purple-600 hover:bg-purple-500 text-white p-4 rounded-xl transition-all shadow-lg active:scale-95 flex flex-col items-center gap-1"
                            >
                                <span className="text-lg font-bold font-heebo relative z-10">{t('credit')}</span>
                                <span className="text-[10px] opacity-80 uppercase tracking-widest relative z-10">Credit / Circled</span>
                                <div className="absolute inset-0 bg-gradient-to-tr from-black/10 to-transparent"></div>
                            </button>
                        </div>

                        <button
                            onClick={() => {
                                setShowBuyinMethodModal(false);
                                setShowBuyinForm(true);
                            }}
                            className="w-full text-slate-500 hover:text-slate-300 text-xs font-bold py-2 transition-colors font-heebo"
                        >
                            {t('backToAmount')}
                        </button>
                    </div>
                </div>
            )}
            {/* ── Player Detail Modal (Enlarged View) ── */}
            {selectedPlayerDetail && (
                <div 
                    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-xl p-4"
                    onClick={() => setSelectedPlayerDetail(null)}
                >
                    <div 
                        className="bg-slate-900 border border-slate-700/50 rounded-2xl w-full max-w-sm flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.5)] animate-in fade-in zoom-in duration-300 overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-6 bg-gradient-to-b from-slate-800/50 to-transparent border-b border-white/5 relative">
                            <button 
                                onClick={() => setSelectedPlayerDetail(null)}
                                className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                            <div className="flex flex-col items-center text-center" dir="rtl">
                                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-4 shadow-inner">
                                    <span className="text-xl font-black text-purple-500 tabular-nums">
                                        {selectedPlayerDetail.index}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-black text-white mb-1 tracking-tight">
                                    {selectedPlayerDetail.player.name}
                                </h3>
                                <div className="text-slate-400 text-xs font-bold uppercase tracking-widest opacity-60">
                                    {t('activePlayerDetails')}
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 flex-1 overflow-y-auto min-h-0 space-y-6">
                            {/* Stats Summary */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-800/40 rounded-xl p-4 border border-white/5 text-center">
                                    <div className="text-[10px] text-slate-500 uppercase font-black mb-1">{t('totalBuyins')}</div>
                                    <div className="text-xl font-black text-emerald-400 tabular-nums">
                                        {formatCurrency(selectedPlayerDetail.buyins.reduce((s, b) => s + b.amount, 0))}
                                    </div>
                                </div>
                                <div className="bg-slate-800/40 rounded-xl p-4 border border-white/5 text-center">
                                    <div className="text-[10px] text-slate-500 uppercase font-black mb-1">{t('actions')}</div>
                                    <div className="text-xl font-black text-purple-400 tabular-nums">
                                        {selectedPlayerDetail.buyins.length}
                                    </div>
                                </div>
                            </div>

                            {/* Buy-in History List */}
                            <div dir="rtl">
                                <h4 className="text-[10px] text-slate-500 uppercase font-black mb-4 tracking-wider flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                                    {t('buyinHistory')}
                                </h4>
                                <div className="space-y-3">
                                    {[...selectedPlayerDetail.buyins].sort((a,b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)).map((buyin, i) => (
                                        <div key={buyin.id || i} className="flex items-center justify-between p-3 bg-slate-800/40 rounded-xl border border-white/5 shadow-sm" dir="ltr">
                                            <div className="flex flex-col text-left">
                                                <span className={clsx(
                                                    "text-lg font-black tabular-nums leading-none mb-1",
                                                    buyin.method === 'credit' ? 'text-purple-400' : 'text-emerald-400'
                                                )}>
                                                    {formatCurrency(buyin.amount)}
                                                </span>
                                                <span className="text-[10px] text-slate-500 font-bold uppercase transition-colors">
                                                    {buyin.method === 'credit' ? t('credit') : t('cash')}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-black text-white tabular-nums">
                                                    {buyin.timestamp ? new Date(buyin.timestamp).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                                                </div>
                                                <div className="text-[9px] text-slate-500 font-bold opacity-60 tabular-nums">
                                                    TIME
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 bg-slate-800/30 border-t border-white/5">
                            <button 
                                onClick={() => setSelectedPlayerDetail(null)}
                                className="w-full py-4 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-black text-sm uppercase tracking-widest transition-all shadow-xl"
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal 
                isOpen={confirmState.isOpen}
                onClose={() => setConfirmState(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmState.onConfirm}
                title={confirmState.title}
                message={confirmState.message}
                type={confirmState.type}
            />
        </div>
        </>
    );
};
